import './style.modules.scss'

import {
  BrandIcon,
  BrandIconId,
  Link,
  LinkSize,
  LinkVariant,
  useFocusTrap,
  useSetBodyScroll
} from '@ampcore/adel-react'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useAppContext } from '../../context/useAppContext'
import { makeUrlRelative } from '../../lib/CfgApi/makeUrlRelative'
import { setLocalUserStatus } from '../../lib/helpers/userStatusStorage'
import { MenuItem } from '../../types/MenuItem.type'
import { UserStatus } from '../../types/UserStatus.enum'
import { AppMenu, AppMenuProps } from '../AppMenu/AppMenu'
import { AppMenuMobile } from '../AppMenuMobile/AppMenuMobile'
import { ConditionalLink, ConditionalLinkProps } from '../Link/ConditionalLink'
import { MenuButton } from '../MenuButton/MenuButton'
import { SearchPanelProps } from '../SearchPanel/SearchPanel'
import { AppBarIcons } from './AppBarIcons'
import { AppBarVariant } from './AppBarVariant.type'
import { PersonalizedProspectDropdown } from './PersonalizedProspectDropdown'
import { createSiteNavAnalyticsClickHandlers } from './siteNavAnalyticsClickHandlers'

export type AppBarProps = {
  variant?: AppBarVariant
  /** URL for the logo link */
  logoHref: string
  /** Links shown in the top right section */
  links?: Omit<ConditionalLinkProps, 'userStatus'>[]
  /** Enables sticky header behavior */
  stickyHeader?: boolean
  /** Custom analytics name for tracking */
  analyticsName?: string
  /** Desktop menu configuration */
  menu?: Omit<AppMenuProps, 'searchPanel'>
  /** Items rendered at the bottom of the mobile menu */
  mobileBottomMenuItems: MenuItem[]
  /** Search panel configuration */
  searchPanel?: SearchPanelProps
  /** Test ID prefix for targeting elements in tests */
  testId?: string
  /** When false, mobile menu renders inline instead of portaling. Useful inside shadow DOM (shared loader). Defaults to true. */
  usePortal?: boolean
}

const WINDOW_SIZE_TABLET = 767

export const AppBar = (props: AppBarProps) => {
  const {
    mobileBottomMenuItems,
    logoHref,
    links,
    stickyHeader = false,
    searchPanel,
    variant,
    analyticsName,
    testId,
    usePortal = true
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const appBarRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const mobileNavSectionRef = useRef<HTMLDivElement>(null)
  const atb = new AnalyticsTagBuilder()
    .setDefaultName('AppBar')
    .setAnalyticsName(analyticsName)

  const [, setDimensions] = useState({
    width: appBarRef.current?.clientWidth,
    height: appBarRef.current?.clientWidth
  })

  const { state: appState } = useAppContext()
  const { handleHeaderClick, handleMenuClick } =
    createSiteNavAnalyticsClickHandlers()
  const setBodyScrollLock = useSetBodyScroll(false)

  const closeMenu = useCallback(() => {
    setIsOpen(false)
    setBodyScrollLock(false)
  }, [setBodyScrollLock])

  const openMenu = () => {
    setIsOpen(true)
    setBodyScrollLock(true)
  }

  const handleMenuButtonClick = () => {
    if (isOpen) {
      closeMenu()
      return
    }
    openMenu()
  }

  useEffect(() => {
    const root = document.body
    if (isOpen) {
      root.classList.add('is-appMobileMenuOpen')
    } else {
      root.classList.remove('is-appMobileMenuOpen')
    }

    if (usePortal && isOpen && mobileNavSectionRef.current) {
      // Portal: menu is a direct child of document.body, inert everything else
      const siblings = Array.from(root.children).filter(
        el => el !== mobileNavSectionRef.current && el.tagName !== 'SCRIPT'
      )
      siblings.forEach(el => el.setAttribute('inert', ''))
      return () => {
        root.classList.remove('is-appMobileMenuOpen')
        siblings.forEach(el => el.removeAttribute('inert'))
      }
    }

    return () => {
      root.classList.remove('is-appMobileMenuOpen')
    }
  }, [isOpen, usePortal])

  useFocusTrap(isOpen, mobileNavSectionRef, !usePortal)

  useEffect(() => {
    const onResize = () => {
      setDimensions(() => {
        const width = appBarRef.current?.clientWidth
        const height = appBarRef.current?.clientHeight
        if (width && width >= WINDOW_SIZE_TABLET) {
          closeMenu()
        }
        return { width, height }
      })
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu()
      }
    }

    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, closeMenu])

  return (
    <>
      <AppBarIcons />
      <header
        className={`AppBar${stickyHeader ? ' u-height' : ''}`}
        {...atb.dataTargetAnalyticAttribute()}
        ref={appBarRef}
      >
        <div className={`${stickyHeader ? 'AppBar--sticky' : ''}`}>
          <div className="AppBar-topSection u-bgColorWhite">
            <div className="u-sizeConstrained">
              <div className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween u-sm-paddingRight0">
                {variant !== AppBarVariant.CAMPAIGN_PAGE && (
                  <MenuButton
                    className="u-lg-hidden"
                    open={false}
                    onClick={handleMenuButtonClick}
                    ref={menuButtonRef}
                    testId={testId ? `${testId}-menu-button` : undefined}
                  />
                )}
                <div className="u-flex u-sm-flexAlignSelfCenter">
                  <a
                    href={logoHref}
                    {...atb.dataAnalyticAttribute('AmpLog')}
                    data-testid={testId ? `${testId}-logo` : null}
                  >
                    <BrandIcon
                      className="AppBar-logoDesktop u-sm-hidden u-md-hidden"
                      icon={BrandIconId.AMPF_LOGO}
                      style={{
                        width: '150px',
                        height: '52px',
                        fill: '#003f6b'
                      }}
                      spriteUrl={''}
                      ariaLabel="Ameriprise Financial"
                      testId={testId ? `${testId}-mobile` : undefined}
                    />
                    <BrandIcon
                      className="AppBar-logoMobile u-lg-hidden"
                      icon={BrandIconId.AMPF_LOGO}
                      style={{
                        width: '125px',
                        height: '44px',
                        fill: '#003f6b'
                      }}
                      spriteUrl={''}
                      ariaLabel={'Ameriprise Financial'}
                      testId={testId ? `${testId}-large` : undefined}
                    />
                  </a>
                </div>
                <div className="AppHeader-topRightSection">
                  <div className="u-md-flexRow u-lg-flexRow">
                    {(variant !== AppBarVariant.CAMPAIGN_PAGE ||
                      (links?.length ?? 0) > 0) && (
                      <ul className="u-flex u-flexAlignItemsCenter u-flexJustifyCenter">
                        {variant !== AppBarVariant.CAMPAIGN_PAGE && (
                          <>
                            <li className="u-hidden u-lg-block u-paddingHoriz8">
                              <div className="u-flex">
                                <span
                                  className="adel-material-symbols-16 u-textColorActionBlue u-paddingRight20"
                                  aria-hidden="true"
                                >
                                  security
                                </span>{' '}
                                <a
                                  className="Link u-textPrimary u-linkClean u-flex u-flexJustifyCenter u-flexAlignItemsCenter"
                                  href={makeUrlRelative(
                                    'https://www.ameriprise.com/privacy-security-fraud/online-security-guarantee'
                                  )}
                                  data-qa="OnlineSecurityURL"
                                  {...atb.dataAnalyticAttribute(
                                    'OnlineSecurityGuarantee'
                                  )}
                                  onClick={handleHeaderClick}
                                >
                                  Online security guarantee
                                </a>
                              </div>
                            </li>
                            {appState.ampUserStatus ===
                              UserStatus.CLIENT_ACTIVE && (
                              <>
                                <li className="u-hidden u-lg-block u-paddingHoriz8 u-borderLeft">
                                  <a
                                    className="Link u-textPrimary u-linkClean"
                                    href={makeUrlRelative(
                                      'https://www.ameriprise.com/secure/help'
                                    )}
                                    {...atb.dataAnalyticAttribute('Help')}
                                    onClick={handleHeaderClick}
                                  >
                                    Help
                                  </a>
                                </li>
                                {appState.userData?.data?.firstName &&
                                  !appState.userData?.data?.isOrgClient && (
                                    <li className="u-hidden u-lg-block u-paddingHoriz8 u-borderLeft u-textColorActionBlue">
                                      Hi,{' '}
                                      <Link
                                        className="u-textUppercase"
                                        url={makeUrlRelative(
                                          'https://www.ameriprise.com/secure/profile'
                                        )}
                                        name={appState.userData.data.firstName}
                                        analyticsName={atb.dataAnalyticAttributeValue(
                                          'Profile'
                                        )}
                                        testId={
                                          testId
                                            ? `${testId}-profile-link`
                                            : undefined
                                        }
                                        onClick={handleHeaderClick}
                                      />
                                    </li>
                                  )}
                              </>
                            )}
                            {appState.ampUserStatus !==
                              UserStatus.CLIENT_ACTIVE && (
                              <li className="u-hidden u-lg-block u-paddingHoriz8 u-borderLeft">
                                <a
                                  className="Link u-textPrimary u-linkClean"
                                  href={makeUrlRelative(
                                    'https://www.ameriprise.com/customer-service'
                                  )}
                                  {...atb.dataAnalyticAttribute(
                                    'CustomerService'
                                  )}
                                  onClick={handleHeaderClick}
                                >
                                  Customer Service
                                </a>
                              </li>
                            )}
                          </>
                        )}
                        {links?.map(
                          (
                            link: Omit<ConditionalLinkProps, 'userStatus'>,
                            index: number
                          ) => (
                            <li key={link.url}>
                              {appState.ampUserStatus === UserStatus.PROSPECT &&
                              link.variant === LinkVariant.PRIMARY &&
                              link.name === 'Find an Advisor' &&
                              appState.advisorData?.name &&
                              (appState.advisorData?.url ||
                                appState.advisorData?.email ||
                                appState.advisorData?.phonenumber) ? (
                                <PersonalizedProspectDropdown
                                  advisorData={appState.advisorData}
                                  className="u-marginLeft20 u-sm-hidden u-md-hidden"
                                  testId={
                                    testId
                                      ? `${testId}-personalized-prospect-dropdown`
                                      : undefined
                                  }
                                />
                              ) : (
                                <ConditionalLink
                                  {...link}
                                  userStatus={appState.ampUserStatus!}
                                  className={`u-marginLeft20 ${
                                    link.className || ''
                                  }`}
                                  href={makeUrlRelative(link.url)}
                                  analyticsName={link.analyticsName}
                                  size={LinkSize.SMALL}
                                  testId={
                                    testId
                                      ? `${testId}-link-${index}`
                                      : undefined
                                  }
                                  onClick={e => {
                                    if (link.name === 'Log Out') {
                                      setLocalUserStatus(
                                        UserStatus.CLIENT_EXPIRED
                                      )
                                    }
                                    handleHeaderClick(e)
                                  }}
                                />
                              )}
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {appState.menuItems && (
            <AppMenu
              className="u-hidden u-lg-block"
              menuItems={appState.menuItems}
              searchPanel={searchPanel}
              stickyLinks={links}
              onMenuClick={handleMenuClick}
            />
          )}
          <AppMenuMobile
            userStatus={appState.ampUserStatus}
            className="u-block u-lg-hidden"
            searchPanel={searchPanel}
            menuItems={appState.menuItems?.filter(
              menuItem => menuItem.name !== 'Home'
            )}
            mobileBottomMenuItems={mobileBottomMenuItems}
            isOpen={isOpen}
            testId={testId}
            mobileNavSectionRef={mobileNavSectionRef}
            advisorData={appState.advisorData}
            userData={appState.userData}
            onMenuClick={handleMenuClick}
            onClose={closeMenu}
            logoHref={logoHref}
            links={links}
            usePortal={usePortal}
          />
        </div>
      </header>
    </>
  )
}
